import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { formatCurrency, formatDate } from '@/lib/formatters';
import {
  dashboardLocacoesService,
  type DashboardLocacaoRow,
} from '@/services/dashboardLocacoesService';
import ClienteFornecedorModal from '@/components/ClientesFornecedores/ClienteFornecedorModal';
import type { ClienteFornecedorWithDetails } from '@/types';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Car,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';

interface LocacoesTabProps {
  companyId?: string;
}

type SortKey =
  | 'nomeEmpresa'
  | 'nomeCliente'
  | 'placaEquipamento'
  | 'modelo'
  | 'statusAtual'
  | 'dataInicioContrato'
  | 'dataFimContratoEsperado'
  | 'nomeVendedor'
  | 'porcentagemPago'
  | 'valorTotalPago'
  | 'totalVencido'
  | 'totalAberto'
  | 'totalMultas'
  | 'multasPagas'
  | 'multasVencidas'
  | 'contatosRecebidos'
  | 'quantidadeOs'
  | 'statusDocumentoClickSign'
  | 'dataCancelamento';

type SortDir = 'asc' | 'desc';

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

function sortValue(row: DashboardLocacaoRow, key: SortKey): string | number {
  switch (key) {
    case 'modelo':
      return [row.fabricanteEquipamento, row.modeloEquipamento]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('pt-BR');
    case 'nomeEmpresa':
    case 'nomeCliente':
    case 'placaEquipamento':
    case 'statusAtual':
    case 'nomeVendedor':
    case 'statusDocumentoClickSign':
      return String(row[key] ?? '').toLocaleLowerCase('pt-BR');
    case 'dataInicioContrato':
    case 'dataFimContratoEsperado':
    case 'dataCancelamento': {
      const v = row[key];
      if (!v) return 0;
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    }
    case 'porcentagemPago':
    case 'valorTotalPago':
    case 'totalVencido':
    case 'totalAberto':
    case 'totalMultas':
    case 'multasPagas':
    case 'multasVencidas':
    case 'contatosRecebidos':
    case 'quantidadeOs':
      return Number(row[key]) || 0;
    default:
      return '';
  }
}

function compareRows(a: DashboardLocacaoRow, b: DashboardLocacaoRow, key: SortKey, dir: SortDir): number {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  let cmp = 0;
  if (typeof av === 'number' && typeof bv === 'number') {
    cmp = av - bv;
  } else {
    cmp = String(av).localeCompare(String(bv), 'pt-BR', { sensitivity: 'base', numeric: true });
  }
  return dir === 'asc' ? cmp : -cmp;
}

type ColumnDef = {
  key: SortKey;
  label: string;
  align?: 'left' | 'right';
};

const COLUMNS: ColumnDef[] = [
  { key: 'nomeEmpresa', label: 'Empresa' },
  { key: 'nomeCliente', label: 'Cliente' },
  { key: 'placaEquipamento', label: 'Placa' },
  { key: 'modelo', label: 'Modelo' },
  { key: 'statusAtual', label: 'Status' },
  { key: 'dataInicioContrato', label: 'Início' },
  { key: 'dataFimContratoEsperado', label: 'Fim' },
  { key: 'nomeVendedor', label: 'Vendedor' },
  { key: 'porcentagemPago', label: '% pago', align: 'right' },
  { key: 'valorTotalPago', label: 'Valor pago', align: 'right' },
  { key: 'totalVencido', label: 'Vencido', align: 'right' },
  { key: 'totalAberto', label: 'Em aberto', align: 'right' },
  { key: 'totalMultas', label: 'Multas', align: 'right' },
  { key: 'multasPagas', label: 'Multas pagas', align: 'right' },
  { key: 'multasVencidas', label: 'Multas vencidas', align: 'right' },
  { key: 'contatosRecebidos', label: 'Contatos', align: 'right' },
  { key: 'quantidadeOs', label: 'Qtd O.S.', align: 'right' },
  { key: 'statusDocumentoClickSign', label: 'ClickSign' },
  { key: 'dataCancelamento', label: 'Cancelamento' },
];

export const LocacoesTab: React.FC<LocacoesTabProps> = ({ companyId }) => {
  const [statusAtual, setStatusAtual] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('dataInicioContrato');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedCliente, setSelectedCliente] = useState<ClienteFornecedorWithDetails | null>(
    null
  );
  const [isClienteModalOpen, setIsClienteModalOpen] = useState(false);
  const limit = 50;

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['dashboard-locacoes', companyId, statusAtual, search, page, limit],
    queryFn: () =>
      dashboardLocacoesService.list({
        companyId: companyId || undefined,
        statusAtual: statusAtual || undefined,
        search: search || undefined,
        page,
        limit,
      }),
    placeholderData: (prev) => prev,
  });

  const totalPages = useMemo(() => {
    const total = data?.total ?? 0;
    return Math.max(1, Math.ceil(total / limit));
  }, [data?.total]);

  const statuses = data?.statuses ?? [];
  const items: DashboardLocacaoRow[] = data?.items ?? [];

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => compareRows(a, b, sortKey, sortDir));
    return copy;
  }, [items, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key.startsWith('data') || key.includes('valor') || key.includes('total') || key.includes('porcentagem') || key.includes('multas') || key.includes('contatos') || key.includes('quantidade')
        ? 'desc'
        : 'asc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) {
      return <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden />;
    }
    return sortDir === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-primary" aria-hidden />
    ) : (
      <ArrowDown className="h-3 w-3 text-primary" aria-hidden />
    );
  };

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openClienteModal = (row: DashboardLocacaoRow) => {
    if (!row.clienteFornecedorId) return;
    setSelectedCliente({
      id: row.clienteFornecedorId,
      codigo: '',
      nome: row.nomeCliente || 'Cliente',
      descricao: '',
      whatsapp: '',
      email: '',
      companyId: row.companyId || companyId || '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      company: {
        id: row.companyId || companyId || '',
        name: row.nomeEmpresa || 'Empresa',
      },
      titulosEmAberto: 0,
      valorTotalEmAberto: 0,
      titulosVencidos: 0,
      valorTotalVencido: 0,
      totalTitulos: 0,
      titulos: [],
      feedbacks: [],
      historicoContatos: [],
      _count: { feedbacks: 0, titulos: 0 },
    } as unknown as ClienteFornecedorWithDetails);
    setIsClienteModalOpen(true);
  };

  const closeClienteModal = () => {
    setIsClienteModalOpen(false);
    setSelectedCliente(null);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Car className="h-5 w-5" />
            Locações
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Lista paginada com filtros por status e busca. O filtro de empresa acima vale para esta aba.
            Valores financeiros vêm dos títulos vinculados; O.S. mostra apenas quantidade.
            Clique no cabeçalho para ordenar (asc/desc) na página atual.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Status da locação</label>
              <Select
                value={statusAtual}
                onValueChange={(v) => {
                  setStatusAtual(v);
                  setPage(1);
                }}
                className="w-56"
              >
                <option value="">Todos</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Buscar</label>
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applySearch();
                    }}
                    placeholder="Cliente, placa, empresa ou ID da locação…"
                    className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={applySearch}
                  className="rounded-md border border-border bg-muted px-3 py-2 text-sm font-medium hover:bg-muted/80"
                >
                  Buscar
                </button>
              </div>
            </div>
            <div className="text-sm text-muted-foreground lg:ml-auto">
              {data ? (
                <>
                  {data.total.toLocaleString('pt-BR')} locação(ões)
                  {isFetching && !isLoading ? ' · atualizando…' : null}
                </>
              ) : null}
            </div>
          </div>

          {isLoading && (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-muted" />
              ))}
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center py-10 text-center">
              <AlertCircle className="mb-3 h-10 w-10 text-red-500" />
              <p className="font-medium">Erro ao carregar locações</p>
              <p className="text-sm text-muted-foreground">
                {(error as Error)?.message || 'Tente novamente em instantes.'}
              </p>
            </div>
          )}

          {!isLoading && !error && items.length === 0 && (
            <div className="py-10 text-center text-muted-foreground">
              Nenhuma locação encontrada para os filtros atuais.
            </div>
          )}

          {!isLoading && !error && items.length > 0 && (
            <>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="min-w-[1600px] w-full text-left text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className={`px-3 py-2 whitespace-nowrap ${col.align === 'right' ? 'text-right' : ''}`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleSort(col.key)}
                            className={`inline-flex items-center gap-1 font-medium hover:text-foreground ${
                              col.align === 'right' ? 'flex-row-reverse' : ''
                            } ${sortKey === col.key ? 'text-foreground' : ''}`}
                            aria-label={`Ordenar por ${col.label}`}
                          >
                            <span>{col.label}</span>
                            <SortIcon col={col.key} />
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((row) => (
                      <tr key={row.id} className="border-t border-border hover:bg-muted/30">
                        <td className="px-3 py-2 whitespace-nowrap max-w-[160px] truncate" title={row.nomeEmpresa ?? ''}>
                          {row.nomeEmpresa || '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap max-w-[180px] truncate" title={row.nomeCliente ?? ''}>
                          {row.clienteFornecedorId && row.nomeCliente ? (
                            <button
                              type="button"
                              onClick={() => openClienteModal(row)}
                              className="text-left font-medium text-primary hover:underline truncate max-w-full"
                            >
                              {row.nomeCliente}
                            </button>
                          ) : (
                            row.nomeCliente || '—'
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium">
                          {row.placaEquipamento || '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap max-w-[140px] truncate">
                          {[row.fabricanteEquipamento, row.modeloEquipamento].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                            {row.statusAtual || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {formatDate(row.dataInicioContrato)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {formatDate(row.dataFimContratoEsperado)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap max-w-[140px] truncate">
                          {row.nomeVendedor || '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums">
                          {formatPct(row.porcentagemPago)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums">
                          {formatCurrency(row.valorTotalPago)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-red-600 dark:text-red-400">
                          {formatCurrency(row.totalVencido)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums">
                          {formatCurrency(row.totalAberto)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums">
                          {formatCurrency(row.totalMultas)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums">
                          {formatCurrency(row.multasPagas)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-red-600 dark:text-red-400">
                          {formatCurrency(row.multasVencidas)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums">
                          {row.contatosRecebidos}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums">
                          {row.quantidadeOs}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.statusDocumentoClickSign || '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap max-w-[160px] truncate" title={row.motivoCancelamento ?? ''}>
                          {row.dataCancelamento
                            ? formatDate(row.dataCancelamento)
                            : '—'}
                          {row.motivoCancelamento ? ` · ${row.motivoCancelamento}` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                  {' · '}
                  Ordenado por {COLUMNS.find((c) => c.key === sortKey)?.label} (
                  {sortDir === 'asc' ? 'crescente' : 'decrescente'})
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {selectedCliente && isClienteModalOpen && (
        <ClienteFornecedorModal
          cliente={selectedCliente}
          isOpen={isClienteModalOpen}
          onClose={closeClienteModal}
        />
      )}
    </div>
  );
};
